import React from 'react';
import { Seat } from './Seat';
import { IBlockDto, ISeatReservationDto, BlockDtoSeatCountDirection, RestrictionDto } from '../../../clients/services';
import { ISeatDisplayInfo, IBlockDisplayInfo } from '../services/plan-info';
import { BlockChecker } from '../services/block-checker';

interface BlockProps {
    block: IBlockDto;
    blockInfo: IBlockDisplayInfo;
    restrictions?: RestrictionDto;
    reservations: ISeatReservationDto[];
    ownReservations: ISeatReservationDto[];
    onClickSeat: (seatInfo: ISeatDisplayInfo) => void;
    devMode: boolean;
    onContextMenuSeat?: (event: React.MouseEvent<HTMLDivElement>, seatInfo: ISeatDisplayInfo) => void;
    onClickGap?: (gapInfo: { blockId: number; row: number; seatToShift: number }) => void;
    onClickRemovedSeat?: (removedSeatInfo: { blockId: number; row: number; seat: number }) => void;
    onClickRow?: (rowInfo: { blockId: number; row: number }) => void;
    stopInnerClickPropagation?: boolean;
    selectedRow?: number;
    seatRotation?: number;
}

export class Block extends React.Component<BlockProps> {
    private getTableRowNr(blockChecker: BlockChecker): number {
        return blockChecker.startRowCountAt();
    }

    private createTableLayoutItems(
        blockChecker: BlockChecker,
    ): Array<
        | { kind: 'seat'; seatInfo: ISeatDisplayInfo }
        | { kind: 'gap'; seatToShift: number; gapIndex: number }
        | { kind: 'removed'; seatNr: number }
    > {
        const block = this.props.block;
        const rowNr = this.getTableRowNr(blockChecker);
        const rowSettings = this.props.block.rowSettings?.find((r) => r.row === rowNr);
        const seatInfoBySeat = new Map<number, ISeatDisplayInfo>();
        this.getTableSeatInfos().forEach((seatInfo) => {
            seatInfoBySeat.set(seatInfo.seat, seatInfo);
        });

        const rowSeats: number[] = [];
        if (block.seatCountDirection === BlockDtoSeatCountDirection.LeftToRight) {
            for (
                let seatNr = blockChecker.startSeatCountAt(rowNr);
                seatNr <= blockChecker.endSeatCountAt(rowNr);
                seatNr++
            ) {
                rowSeats.push(seatNr);
            }
        } else {
            for (
                let seatNr = blockChecker.endSeatCountAt(rowNr);
                seatNr >= blockChecker.startSeatCountAt(rowNr);
                seatNr--
            ) {
                rowSeats.push(seatNr);
            }
        }

        const items: Array<
            | { kind: 'seat'; seatInfo: ISeatDisplayInfo }
            | { kind: 'gap'; seatToShift: number; gapIndex: number }
            | { kind: 'removed'; seatNr: number }
        > = [];

        if (block.seatCountDirection === BlockDtoSeatCountDirection.LeftToRight) {
            for (const seatNr of rowSeats) {
                if (rowSettings?.gaps?.length) {
                    const gap = rowSettings.gaps.find((g) => g.seatToShift === seatNr);
                    if (gap) {
                        for (let gapNr = 0; gapNr < gap.shiftNrOfGaps; gapNr++) {
                            items.push({ kind: 'gap', seatToShift: gap.seatToShift, gapIndex: gapNr });
                        }
                    }
                }

                const seatInfo = seatInfoBySeat.get(seatNr);
                if (seatInfo) {
                    items.push({ kind: 'seat', seatInfo });
                } else {
                    items.push({ kind: 'removed', seatNr });
                }
            }
        } else {
            for (const seatNr of rowSeats) {
                if (rowSettings?.gaps?.length) {
                    const gapBefore = rowSettings.gaps.find((g) => g.seatToShift - 1 === seatNr);
                    if (gapBefore) {
                        for (let gapNr = 0; gapNr < gapBefore.shiftNrOfGaps; gapNr++) {
                            items.push({ kind: 'gap', seatToShift: gapBefore.seatToShift, gapIndex: gapNr });
                        }
                    }
                }

                const seatInfo = seatInfoBySeat.get(seatNr);
                if (seatInfo) {
                    items.push({ kind: 'seat', seatInfo });
                } else {
                    items.push({ kind: 'removed', seatNr });
                }

                if (blockChecker.startSeatCountAt(rowNr) === seatNr && rowSettings?.gaps?.length) {
                    const gapAfter = rowSettings.gaps.find((g) => g.seatToShift === seatNr);
                    if (gapAfter) {
                        for (let gapNr = 0; gapNr < gapAfter.shiftNrOfGaps; gapNr++) {
                            items.push({ kind: 'gap', seatToShift: gapAfter.seatToShift, gapIndex: gapNr });
                        }
                    }
                }
            }
        }

        return items;
    }

    private renderTableLayoutItem(
        item:
            | { kind: 'seat'; seatInfo: ISeatDisplayInfo }
            | { kind: 'gap'; seatToShift: number; gapIndex: number }
            | { kind: 'removed'; seatNr: number },
        rowNr: number,
        key: string,
    ): JSX.Element {
        if (item.kind === 'seat') {
            return (
                <div key={key}>
                    <Seat
                        onClick={this.props.onClickSeat}
                        onContextMenu={this.props.onContextMenuSeat}
                        seatInfo={item.seatInfo}
                        devMode={this.props.devMode}
                        stopClickPropagation={this.props.stopInnerClickPropagation}
                        seatRotation={this.props.seatRotation}
                    />
                </div>
            );
        }

        if (item.kind === 'gap') {
            return (
                <div
                    key={key}
                    title={this.props.onClickGap ? `Gap bei Sitz ${item.seatToShift} entfernen` : undefined}
                    onClick={(event) => {
                        if (this.props.stopInnerClickPropagation) {
                            event.stopPropagation();
                        }
                        this.props.onClickGap?.({
                            blockId: this.props.block.id,
                            row: rowNr,
                            seatToShift: item.seatToShift,
                        });
                    }}
                    style={{
                        width: '12px',
                        height: '12px',
                        borderRadius: '50%',
                        border: '1px solid rgba(109, 84, 50, 0.8)',
                        backgroundColor: 'rgba(255, 255, 255, 0.9)',
                        cursor: this.props.onClickGap ? 'pointer' : 'default',
                    }}
                />
            );
        }

        return (
            <div
                key={key}
                title={this.props.onClickRemovedSeat ? `Sitz ${item.seatNr} wiederherstellen` : undefined}
                onClick={(event) => {
                    if (this.props.stopInnerClickPropagation) {
                        event.stopPropagation();
                    }
                    this.props.onClickRemovedSeat?.({
                        blockId: this.props.block.id,
                        row: rowNr,
                        seat: item.seatNr,
                    });
                }}
                style={{
                    width: '14px',
                    height: '14px',
                    lineHeight: '12px',
                    textAlign: 'center',
                    fontSize: '12px',
                    color: '#7e1f1f',
                    border: '1px dashed rgba(126, 31, 31, 0.7)',
                    borderRadius: '3px',
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    cursor: this.props.onClickRemovedSeat ? 'pointer' : 'default',
                    userSelect: 'none',
                }}
            >
                x
            </div>
        );
    }

    private isTableLayout(): boolean {
        return this.props.block.blockLayoutType === 'table';
    }

    private isRadialLayout(): boolean {
        return this.props.block.blockLayoutType === 'radial';
    }

    private createCenterTableOverlay(): JSX.Element | null {
        if (!this.props.block.hasCenterTable && !this.isTableLayout()) {
            return null;
        }

        return (
            <div
                style={{
                    position: 'absolute',
                    left: '50%',
                    top: '50%',
                    transform: 'translate(-50%, -50%)',
                    minWidth: '46px',
                    minHeight: '24px',
                    padding: '2px 8px',
                    borderRadius: '6px',
                    backgroundColor: 'rgba(216, 196, 167, 0.9)',
                    border: '1px solid rgba(109, 84, 50, 0.7)',
                    color: '#3f2c17',
                    fontSize: '11px',
                    lineHeight: 1.2,
                    textAlign: 'center',
                    pointerEvents: 'none',
                    zIndex: 2,
                    boxSizing: 'border-box',
                    whiteSpace: 'nowrap',
                }}
            >
                {this.props.block.centerTableLabel?.trim() || 'Tisch'}
            </div>
        );
    }

    private getTableSeatInfos(): ISeatDisplayInfo[] {
        const row = this.props.blockInfo.rows[0];
        if (!row) {
            return [];
        }
        return row.seatInfos;
    }

    private renderRoundTableSeats(blockChecker: BlockChecker): JSX.Element {
        const rowNr = this.getTableRowNr(blockChecker);
        const tableItems = this.createTableLayoutItems(blockChecker);
        const seatCount = Math.max(1, tableItems.length);
        const radius = Math.max(40, Math.min(68, 30 + seatCount * 3.2));
        const size = Math.ceil(radius * 2 + 36);
        const tableDiameter = Math.max(44, Math.round(radius * 1.08));
        const center = size / 2;

        return (
            <div style={{ position: 'relative', width: `${size}px`, height: `${size}px` }}>
                {tableItems.map((item, index) => {
                    const angle = (index / Math.max(1, tableItems.length)) * Math.PI * 2 - Math.PI / 2;
                    const x = center + Math.cos(angle) * radius;
                    const y = center + Math.sin(angle) * radius;
                    return (
                        <div
                            key={`table-item-round-${this.props.block.id}-${index}`}
                            style={{
                                position: 'absolute',
                                left: `${x}px`,
                                top: `${y}px`,
                                transform: 'translate(-50%, -50%)',
                            }}
                        >
                            {this.renderTableLayoutItem(
                                item,
                                rowNr,
                                `table-item-round-content-${this.props.block.id}-${index}`,
                            )}
                        </div>
                    );
                })}

                <div
                    style={{
                        position: 'absolute',
                        left: '50%',
                        top: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: `${tableDiameter}px`,
                        height: `${tableDiameter}px`,
                        borderRadius: '50%',
                        backgroundColor: 'rgba(216, 196, 167, 0.9)',
                        border: '1px solid rgba(109, 84, 50, 0.7)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '11px',
                        color: '#3f2c17',
                        pointerEvents: 'none',
                    }}
                >
                    {this.props.block.centerTableLabel?.trim() || 'Tisch'}
                </div>
            </div>
        );
    }

    private renderRectTableSeats(blockChecker: BlockChecker): JSX.Element {
        const rowNr = this.getTableRowNr(blockChecker);
        const tableItems = this.createTableLayoutItems(blockChecker);
        const seatCount = Math.max(1, tableItems.length);
        const width = Math.max(176, Math.min(232, Math.round(142 + seatCount * 6)));
        const height = Math.max(118, Math.min(172, Math.round(96 + seatCount * 3.4)));
        const padX = 12;
        const padY = 12;
        const tableWidth = Math.max(84, Math.min(116, Math.round(width * 0.48)));
        const tableHeight = Math.max(44, Math.min(64, Math.round(height * 0.4)));

        const getPosition = (index: number, total: number) => {
            const usableWidth = width - 2 * padX;
            const usableHeight = height - 2 * padY;
            const perimeter = 2 * (usableWidth + usableHeight);
            const distance = ((index + 0.5) / Math.max(1, total)) * perimeter;

            if (distance < usableWidth) {
                return { x: padX + distance, y: padY };
            }

            if (distance < usableWidth + usableHeight) {
                const local = distance - usableWidth;
                return { x: width - padX, y: padY + local };
            }

            if (distance < 2 * usableWidth + usableHeight) {
                const local = distance - (usableWidth + usableHeight);
                return { x: width - padX - local, y: height - padY };
            }

            const local = distance - (2 * usableWidth + usableHeight);
            return { x: padX, y: height - padY - local };
        };

        return (
            <div style={{ position: 'relative', width: `${width}px`, height: `${height}px` }}>
                {tableItems.map((item, index) => {
                    const pos = getPosition(index, tableItems.length);
                    return (
                        <div
                            key={`table-item-rect-${this.props.block.id}-${index}`}
                            style={{
                                position: 'absolute',
                                left: `${pos.x}px`,
                                top: `${pos.y}px`,
                                transform: 'translate(-50%, -50%)',
                            }}
                        >
                            {this.renderTableLayoutItem(
                                item,
                                rowNr,
                                `table-item-rect-content-${this.props.block.id}-${index}`,
                            )}
                        </div>
                    );
                })}

                <div
                    style={{
                        position: 'absolute',
                        left: '50%',
                        top: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: `${tableWidth}px`,
                        height: `${tableHeight}px`,
                        borderRadius: '8px',
                        backgroundColor: 'rgba(216, 196, 167, 0.9)',
                        border: '1px solid rgba(109, 84, 50, 0.7)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '11px',
                        color: '#3f2c17',
                        pointerEvents: 'none',
                    }}
                >
                    {this.props.block.centerTableLabel?.trim() || 'Tisch'}
                </div>
            </div>
        );
    }

    private renderTableLayout(blockChecker: BlockChecker): JSX.Element {
        const title = this.props.block.name;
        const tableSeats =
            this.props.block.tableShape === 'rect'
                ? this.renderRectTableSeats(blockChecker)
                : this.renderRoundTableSeats(blockChecker);
        return (
            <div style={{ display: 'inline-block', position: 'relative', padding: '2px 4px' }}>
                {title && (
                    <div style={{ fontSize: '12px', textAlign: 'left', marginBottom: '4px', opacity: 0.9 }}>
                        {title}
                    </div>
                )}
                {tableSeats}
                {this.props.devMode && (
                    <div style={{ marginTop: '4px', fontSize: '11px' }}>BlockId: {this.props.block.id}</div>
                )}
            </div>
        );
    }

    private renderRadialLayout(): JSX.Element {
        const rows = [...this.props.blockInfo.rows].sort((left, right) => left.row - right.row);
        const radialInnerRadiusRaw = (this.props.block as any).radialInnerRadius;
        const radialRowSpacingRaw = (this.props.block as any).radialRowSpacing;
        const radialArcDegreesRaw = (this.props.block as any).radialArcDegrees;
        const radialOpenTop = (this.props.block as any).radialOpenTop ?? true;
        const rowSpacing =
            Number.isFinite(radialRowSpacingRaw) && radialRowSpacingRaw >= 8 ? Math.round(radialRowSpacingRaw) : 30;
        const radialArcDegrees =
            Number.isFinite(radialArcDegreesRaw) && radialArcDegreesRaw > 0 && radialArcDegreesRaw < 360
                ? Math.round(radialArcDegreesRaw)
                : 220;

        // Open to stage by default (top side), while keeping backwards compatibility via explicit false.
        const centerAngleDeg = radialOpenTop ? 270 : 90;
        const startAngleDeg = centerAngleDeg - radialArcDegrees / 2;
        const endAngleDeg = centerAngleDeg + radialArcDegrees / 2;
        const arcSpanRad = (radialArcDegrees * Math.PI) / 180;
        const firstRowSeatCount = rows[0]?.seatInfos.length ?? Math.max(1, this.props.block.seatsInRow ?? 1);
        const targetSeatSpacing = 20;
        const autoInnerRadius = Math.round(
            Math.max(
                36,
                Math.min(
                    240,
                    firstRowSeatCount <= 1
                        ? 56
                        : (targetSeatSpacing * (firstRowSeatCount - 1)) / Math.max(arcSpanRad, 0.1),
                ),
            ),
        );
        const innerRadius =
            Number.isFinite(radialInnerRadiusRaw) && radialInnerRadiusRaw >= 10
                ? Math.round(radialInnerRadiusRaw)
                : autoInnerRadius;
        const title = this.props.block.name;

        const seatPositions: Array<{ seatInfo: ISeatDisplayInfo; x: number; y: number }> = [];
        let minX = 0;
        let minY = 0;
        let maxX = 0;
        let maxY = 0;

        rows.forEach((rowInfo, rowIndex) => {
            const radius = innerRadius + rowIndex * rowSpacing;
            const seats = rowInfo.seatInfos;
            const seatCount = seats.length;

            seats.forEach((seatInfo, seatIndex) => {
                const angleDeg =
                    seatCount === 1
                        ? (startAngleDeg + endAngleDeg) / 2
                        : startAngleDeg + (seatIndex * (endAngleDeg - startAngleDeg)) / (seatCount - 1);
                const angleRad = (angleDeg * Math.PI) / 180;
                const x = Math.cos(angleRad) * radius;
                const y = -Math.sin(angleRad) * radius;

                minX = Math.min(minX, x);
                minY = Math.min(minY, y);
                maxX = Math.max(maxX, x);
                maxY = Math.max(maxY, y);

                seatPositions.push({ seatInfo, x, y });
            });
        });

        const horizontalPadding = 34;
        const verticalPadding = 24;
        const radialWidth = Math.max(1, maxX - minX + horizontalPadding * 2);
        const radialHeight = Math.max(1, maxY - minY + verticalPadding * 2);

        const seatNodes: JSX.Element[] = [];
        seatPositions.forEach(({ seatInfo, x, y }) => {
            const normalizedX = x - minX + horizontalPadding;
            const normalizedY = y - minY + verticalPadding;

            seatNodes.push(
                <div
                    key={`radial-seat-${seatInfo.blockId}-${seatInfo.row}-${seatInfo.seat}`}
                    style={{
                        position: 'absolute',
                        left: `${normalizedX}px`,
                        top: `${normalizedY}px`,
                        transform: 'translate(-50%, -50%)',
                    }}
                >
                    <Seat
                        onClick={this.props.onClickSeat}
                        onContextMenu={this.props.onContextMenuSeat}
                        seatInfo={seatInfo}
                        devMode={this.props.devMode}
                        stopClickPropagation={this.props.stopInnerClickPropagation}
                        seatRotation={this.props.seatRotation}
                    />
                </div>,
            );
        });

        return (
            <div style={{ display: 'inline-block', position: 'relative', padding: '6px 8px' }}>
                {title && (
                    <div style={{ fontSize: '12px', textAlign: 'left', marginBottom: '4px', opacity: 0.9 }}>
                        {title}
                    </div>
                )}
                <div
                    style={{
                        position: 'relative',
                        width: `${radialWidth}px`,
                        height: `${radialHeight}px`,
                    }}
                >
                    {seatNodes}
                </div>
                {this.props.devMode && (
                    <div style={{ marginTop: '4px', fontSize: '11px' }}>BlockId: {this.props.block.id}</div>
                )}
            </div>
        );
    }

    private hasRowSeatCountOverride(rowNr: number): boolean {
        const rowSetting = this.props.block.rowSettings?.find((r) => r.row === rowNr);
        const override = rowSetting ? (rowSetting as any).seatsInRow : undefined;
        return Number.isInteger(override) && override > 0;
    }

    private calculateEvenSeatPositions(totalSlots: number, seatCount: number): number[] {
        if (seatCount <= 0 || totalSlots <= 0) {
            return [];
        }
        if (seatCount >= totalSlots) {
            return Array.from({ length: totalSlots }, (_, index) => index);
        }
        if (seatCount === 1) {
            return [Math.floor((totalSlots - 1) / 2)];
        }

        const positions: number[] = [];
        for (let index = 0; index < seatCount; index++) {
            const raw = (index * (totalSlots - 1)) / (seatCount - 1);
            let candidate = Math.round(raw);
            if (positions.length > 0 && candidate <= positions[positions.length - 1]) {
                candidate = positions[positions.length - 1] + 1;
            }
            const maxAllowed = totalSlots - (seatCount - index);
            positions.push(Math.min(candidate, maxAllowed));
        }
        return positions;
    }

    private createEvenlySpreadSeats(blockChecker: BlockChecker, rowNr: number, seats: number[]): JSX.Element[] {
        const cells: JSX.Element[] = [];
        const totalSlots = blockChecker.getBlockSeatCount();
        const positions = this.calculateEvenSeatPositions(totalSlots, seats.length);
        const seatByPosition = new Map<number, number>();

        positions.forEach((position, index) => {
            seatByPosition.set(position, seats[index]);
        });

        for (let slot = 0; slot < totalSlots; slot++) {
            const seatNr = seatByPosition.get(slot);
            if (seatNr === undefined) {
                cells.push(
                    <td key={`even-gap-${this.props.block.id}-${rowNr}-${slot}`} className="seats-cell empty">
                        &nbsp;
                    </td>,
                );
                continue;
            }

            if (blockChecker.seatIsRemoved(rowNr, seatNr)) {
                cells.push(this.createRemovedSeatCell(rowNr, seatNr));
            } else {
                cells.push(this.createSeat(rowNr, seatNr));
            }
        }

        return cells;
    }

    createRowSelectorButton(blockId: number, rowNr: number) {
        if (!this.props.onClickRow) {
            return null;
        }

        return (
            <button
                type="button"
                title={`Select row ${rowNr}`}
                onClick={(event) => {
                    if (this.props.stopInnerClickPropagation) {
                        event.stopPropagation();
                    }
                    this.props.onClickRow?.({ blockId, row: rowNr });
                }}
                style={{
                    marginRight: '6px',
                    width: '16px',
                    height: '16px',
                    padding: 0,
                    lineHeight: 1,
                    cursor: 'pointer',
                }}
            >
                •
            </button>
        );
    }

    createGapCell(rowNr: number, seatToShift: number, gapNr: number, keySuffix: string) {
        return (
            <td
                key={`gap-${this.props.block.id}-${rowNr}-${seatToShift}-${gapNr}-${keySuffix}`}
                className="gap-cell"
                title={this.props.onClickGap ? `Gap bei Sitz ${seatToShift} entfernen` : undefined}
                onClick={(event) => {
                    if (this.props.stopInnerClickPropagation) {
                        event.stopPropagation();
                    }
                    this.props.onClickGap?.({
                        blockId: this.props.block.id,
                        row: rowNr,
                        seatToShift: seatToShift,
                    });
                }}
                style={{
                    minWidth: '10px',
                    cursor: this.props.onClickGap ? 'pointer' : undefined,
                }}
            >
                {this.props.onClickGap ? 'o' : ''}
            </td>
        );
    }

    createRemovedSeatCell(rowNr: number, seatNr: number) {
        return (
            <td
                key={`removed-${this.props.block.id}-${rowNr}-${seatNr}`}
                className="seats-cell removed"
                title={this.props.onClickRemovedSeat ? `Sitz ${seatNr} wiederherstellen` : undefined}
                onClick={(event) => {
                    if (this.props.stopInnerClickPropagation) {
                        event.stopPropagation();
                    }
                    this.props.onClickRemovedSeat?.({
                        blockId: this.props.block.id,
                        row: rowNr,
                        seat: seatNr,
                    });
                }}
                style={{
                    minWidth: '10px',
                    cursor: this.props.onClickRemovedSeat ? 'pointer' : undefined,
                    opacity: 0.4,
                }}
            >
                {this.props.onClickRemovedSeat ? 'x' : ''}
            </td>
        );
    }

    createSeatHeaders(blockChecker: BlockChecker) {
        const headers: JSX.Element[] = [];
        const block = this.props.block;
        const maxSeats = blockChecker.getBlockSeatCount();

        const seatStartAt = !block.startSeatNumbersAt ? 1 : block.startSeatNumbersAt;
        if (block.seatCountDirection === BlockDtoSeatCountDirection.LeftToRight) {
            if (!block.showRowNrRight) {
                headers.push(<th key={`seatheader-${block.id}`} className="seat-label"></th>);
            }
            for (let seatNr = seatStartAt; seatNr < maxSeats + seatStartAt; seatNr++) {
                headers.push(
                    <th key={'header-' + seatNr} className="seat-label">
                        S{seatNr}
                    </th>,
                );
            }
        } else {
            if (!block.showRowNrRight) {
                headers.push(<th key={`seatheader-${block.id}`} className="seat-label"></th>);
            }
            for (let seatNr = maxSeats + seatStartAt - 1; seatNr >= seatStartAt; seatNr--) {
                headers.push(
                    <th key={seatNr} className="seat-label">
                        S{seatNr}
                    </th>,
                );
            }
        }
        return headers;
    }

    createSeat(rowNr: number, seatNr: number) {
        const row = this.props.blockInfo.rows.find((r) => r.row === rowNr);
        const seat = row?.seatInfos.find((r) => r.seat === seatNr);
        if (seat) {
            return (
                <td key={`seat-${this.props.blockInfo?.blockId}-${row?.row}-${seat?.seat ?? 0}`} className="seats-cell">
                    <Seat
                        onClick={this.props.onClickSeat}
                        onContextMenu={this.props.onContextMenuSeat}
                        seatInfo={seat}
                        devMode={this.props.devMode}
                        stopClickPropagation={this.props.stopInnerClickPropagation}
                        seatRotation={this.props.seatRotation}
                    />
                </td>
            );
        }
        return (
            <td key={`seat-${this.props.blockInfo?.blockId}-${row?.row}-${seatNr}-empty`} className="seats-cell empty">
                &nbsp;
            </td>
        );
    }

    createRows(blockChecker: BlockChecker) {
        const rows: JSX.Element[] = [];
        const block = this.props.block;
        for (let rowNr = blockChecker.startRowCountAt(); rowNr <= blockChecker.endRowCountAt(); rowNr++) {
            if (blockChecker.isSkipped(rowNr)) {
                const cells: JSX.Element[] = [];

                if (!block.hideRowNr && !block.showRowNrRight) {
                    cells.push(
                        <th
                            key={`row-label-skipped-${rowNr}`}
                            className="row-label"
                            onClick={(event) => {
                                if (this.props.stopInnerClickPropagation) {
                                    event.stopPropagation();
                                }
                                this.props.onClickRow?.({ blockId: block.id, row: rowNr });
                            }}
                            style={{ cursor: this.props.onClickRow ? 'pointer' : undefined }}
                        >
                            {this.createRowSelectorButton(block.id, rowNr)}R{rowNr}
                        </th>,
                    );
                }

                rows.push(
                    <tr
                        key={`${block.id}-${rowNr}-corridor1`}
                        className={`corridor-row ${this.props.selectedRow === rowNr ? 'selected' : ''}`}
                    >
                        {cells}
                        <td
                            className="corridor-cell"
                            colSpan={
                                block.hideRowNr
                                    ? blockChecker.getBlockSeatCount() + 1
                                    : blockChecker.getBlockSeatCount()
                            }
                        >
                            &nbsp;
                        </td>
                        {!block.hideRowNr && block.showRowNrRight && (
                            <th
                                key={`row-label-skipped-right-${rowNr}`}
                                className="row-label"
                                onClick={(event) => {
                                    if (this.props.stopInnerClickPropagation) {
                                        event.stopPropagation();
                                    }
                                    this.props.onClickRow?.({ blockId: block.id, row: rowNr });
                                }}
                                style={{ cursor: this.props.onClickRow ? 'pointer' : undefined }}
                            >
                                {this.createRowSelectorButton(block.id, rowNr)}R{rowNr}
                            </th>
                        )}
                    </tr>,
                );
            } else {
                rows.push(
                    <tr
                        key={`${block.id}-${rowNr}-seats`}
                        className={`seats-row ${this.props.selectedRow === rowNr ? 'selected' : ''}`}
                    >
                        {this.createRow(blockChecker, rowNr)}
                    </tr>,
                );
            }

            if (blockChecker.hasCorridorAfter(rowNr)) {
                rows.push(
                    <tr key={`${block.id}-${rowNr}-corridor2`} className="corridor-row">
                        <td className="corridor-cell" colSpan={blockChecker.getBlockSeatCount() + 1}>
                            &nbsp;
                        </td>
                    </tr>,
                );
            }
        }
        return rows;
    }

    createRow(blockChecker: BlockChecker, rowNr: number) {
        const block = this.props.block;
        const rowSettings = this.props.block.rowSettings?.find((r) => r.row === rowNr);
        const seats: JSX.Element[] = [];
        if (!block.hideRowNr && !block.showRowNrRight) {
            seats.push(
                <th
                    key={'row-label-' + rowNr}
                    className="row-label"
                    onClick={(event) => {
                        if (this.props.stopInnerClickPropagation) {
                            event.stopPropagation();
                        }
                        this.props.onClickRow?.({ blockId: block.id, row: rowNr });
                    }}
                    style={{ cursor: this.props.onClickRow ? 'pointer' : undefined }}
                >
                    {this.createRowSelectorButton(block.id, rowNr)}R{rowNr}
                </th>,
            );
        }
        const hasRowOverride = this.hasRowSeatCountOverride(rowNr);
        const rowSeats: number[] = [];
        if (block.seatCountDirection === BlockDtoSeatCountDirection.LeftToRight) {
            for (
                let seatNr = blockChecker.startSeatCountAt(rowNr);
                seatNr <= blockChecker.endSeatCountAt(rowNr);
                seatNr++
            ) {
                rowSeats.push(seatNr);
            }
        } else {
            for (
                let seatNr = blockChecker.endSeatCountAt(rowNr);
                seatNr >= blockChecker.startSeatCountAt(rowNr);
                seatNr--
            ) {
                rowSeats.push(seatNr);
            }
        }

        if (hasRowOverride) {
            seats.push(...this.createEvenlySpreadSeats(blockChecker, rowNr, rowSeats));
        } else if (block.seatCountDirection === BlockDtoSeatCountDirection.LeftToRight) {
            for (const seatNr of rowSeats) {
                if (rowSettings?.gaps && rowSettings?.gaps?.length > 0) {
                    const gap = rowSettings.gaps.find((g) => g.seatToShift === seatNr);
                    if (gap) {
                        for (let gapNr = 0; gapNr < gap.shiftNrOfGaps; gapNr++) {
                            seats.push(this.createGapCell(rowNr, gap.seatToShift, gapNr, 'ltr-before'));
                        }
                    }
                }
                if (blockChecker.seatIsRemoved(rowNr, seatNr)) {
                    seats.push(this.createRemovedSeatCell(rowNr, seatNr));
                } else {
                    seats.push(this.createSeat(rowNr, seatNr));
                }
            }
        } else {
            for (const seatNr of rowSeats) {
                if (rowSettings?.gaps && rowSettings?.gaps?.length > 0) {
                    const gap = rowSettings.gaps.find((g) => g.seatToShift - 1 === seatNr);
                    if (gap) {
                        for (let gapNr = 0; gapNr < gap.shiftNrOfGaps; gapNr++) {
                            seats.push(this.createGapCell(rowNr, gap.seatToShift, gapNr, 'rtl-before'));
                        }
                    }
                }
                if (blockChecker.seatIsRemoved(rowNr, seatNr)) {
                    seats.push(this.createRemovedSeatCell(rowNr, seatNr));
                } else {
                    seats.push(this.createSeat(rowNr, seatNr));
                }
                if (blockChecker.startSeatCountAt(rowNr) === seatNr) {
                    if (rowSettings?.gaps && rowSettings?.gaps?.length > 0) {
                        const gap = rowSettings.gaps.find((g) => g.seatToShift === seatNr);
                        if (gap) {
                            for (let gapNr = 0; gapNr < gap.shiftNrOfGaps; gapNr++) {
                                seats.push(this.createGapCell(rowNr, gap.seatToShift, gapNr, 'rtl-after'));
                            }
                        }
                    }
                }
            }
        }
        if (!block.hideRowNr && block.showRowNrRight) {
            seats.push(
                <th
                    key={'row-label-right-' + rowNr}
                    className="row-label"
                    onClick={(event) => {
                        if (this.props.stopInnerClickPropagation) {
                            event.stopPropagation();
                        }
                        this.props.onClickRow?.({ blockId: block.id, row: rowNr });
                    }}
                    style={{ cursor: this.props.onClickRow ? 'pointer' : undefined }}
                >
                    {this.createRowSelectorButton(block.id, rowNr)}R{rowNr}
                </th>,
            );
        }
        return seats;
    }

    render() {
        const block = this.props.block;
        const blockChecker = new BlockChecker(block);

        if (this.isTableLayout()) {
            return this.renderTableLayout(blockChecker);
        }

        if (this.isRadialLayout()) {
            return this.renderRadialLayout();
        }

        return (
            <div style={{ display: 'inline-block', position: 'relative' }}>
                {this.createCenterTableOverlay()}
                <table className="seats-block" style={{ display: 'inline' }}>
                    <thead>
                        {(!block.hideName || this.props.devMode) && (
                            <tr className="seats-block-title-row" key={'seats-block-title-row'}>
                                <th
                                    className="seats-block-title"
                                    colSpan={blockChecker.getBlockSeatCount() + 1}
                                    style={{ textAlign: 'left' }}
                                >
                                    {block.name}
                                    {this.props.devMode && <span> BlockId: {block.id}</span>}
                                </th>
                            </tr>
                        )}
                    </thead>
                    <tbody>
                        {!block.hideSeatNr && !block.showSeatNrAtTheEnd && (
                            <tr key={'seats-label-row-header'} className="seats-label-row">
                                {this.createSeatHeaders(blockChecker)}
                            </tr>
                        )}
                        {this.createRows(blockChecker)}
                        {!block.hideSeatNr && block.showSeatNrAtTheEnd && (
                            <tr key={'seats-label-row-footer'} className="seats-label-row">
                                {this.createSeatHeaders(blockChecker)}
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        );
    }
}
